<script setup>
import { inject, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';

const { t } = useI18n();

const props = defineProps({
  actionName: { type: String, default: '' },
  modelValue: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['update:modelValue']);

inject('flowTriggerEvent', null);
inject('flowDynamicVariables', null);
const flowWebhookToken = inject('flowWebhookToken', computed(() => ''));
const flowWebhookSecret = inject('flowWebhookSecret', computed(() => ''));

const UNIT_OPTIONS = computed(() => [
  { id: 'minutes', name: t('FLOW_BUILDER.ACTION_FORMS.WAIT_EXTERNAL_EVENT.UNIT_MINUTES') },
  { id: 'hours', name: t('FLOW_BUILDER.ACTION_FORMS.WAIT_EXTERNAL_EVENT.UNIT_HOURS') },
  { id: 'days', name: t('FLOW_BUILDER.ACTION_FORMS.WAIT_EXTERNAL_EVENT.UNIT_DAYS') },
]);

function update(patch) {
  emit('update:modelValue', { ...props.modelValue, ...patch });
}

const selectedUnit = computed(() => UNIT_OPTIONS.value.find(u => u.id === (props.modelValue?.timeout_unit ?? 'hours')) ?? null);

const callbackUrlSample = computed(
  () => `{origin}/webhooks/flow_builder/${flowWebhookToken.value}/callback/{{variables.__ext_event_id}}`
);
const SIGNATURE_HEADER = 'X-Flow-Signature';
const SIGNATURE_SAMPLE = 'X-Flow-Signature: sha256=HMAC_SHA256(body, secret)';
const PYTHON_SAMPLE = "hmac.new(secret.encode(), body, 'sha256').hexdigest()";
const CORRELATION_ID_TOKEN = 'correlation_id';
const EXT_EVENT_ID_VAR = '{{variables.__ext_event_id}}';
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Timeout -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.WAIT_EXTERNAL_EVENT.TIMEOUT') }}</span>
      <div class="flex gap-2 items-center">
        <input
          type="number"
          min="1"
          class="w-16 h-9 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
          :value="modelValue?.timeout_value ?? 24"
          @input="update({ timeout_value: parseInt($event.target.value) || 24 })"
        />
        <div class="multiselect-wrap--small flex-1">
          <MultiselectDropdown
            :options="UNIT_OPTIONS"
            :selected-item="selectedUnit"
            :has-thumbnail="false"
            :hide-search="true"
            @select="v => update({ timeout_unit: v?.id ?? 'hours' })"
          />
        </div>
      </div>
    </div>

    <!-- Variable name -->
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.WAIT_EXTERNAL_EVENT.SAVE_BODY_IN_VARIABLE') }}</span>
      <input
        class="reset-base px-3 py-2 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus:outline-n-brand text-sm text-n-slate-12 placeholder:text-n-slate-9"
        :value="modelValue?.variable_name ?? 'external_event'"
        :placeholder="t('FLOW_BUILDER.ACTION_FORMS.WAIT_EXTERNAL_EVENT.VARIABLE_NAME_PLACEHOLDER')"
        @input="update({ variable_name: $event.target.value })"
      />
    </div>

    <!-- Info block -->
    <div class="rounded-lg bg-n-alpha-1 border border-n-weak p-3 flex flex-col gap-2">
      <p class="text-xs font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.WAIT_EXTERNAL_EVENT.HOW_TO_USE_WEBHOOK') }}</p>
      <p class="text-xs text-n-slate-10 leading-relaxed">
        {{ t('FLOW_BUILDER.ACTION_FORMS.WAIT_EXTERNAL_EVENT.WEBHOOK_INSTRUCTIONS') }}
      </p>
      <code class="text-[10px] font-mono bg-n-solid-2 rounded px-2 py-1 text-n-slate-11 break-all">
        {{ callbackUrlSample }}
      </code>

      <div class="h-px bg-n-weak" />

      <p class="text-xs font-medium text-n-slate-12">{{ t('FLOW_BUILDER.ACTION_FORMS.WAIT_EXTERNAL_EVENT.HMAC_REQUIRED') }}</p>
      <p class="text-xs text-n-slate-10 leading-relaxed">
        {{ t('FLOW_BUILDER.ACTION_FORMS.WAIT_EXTERNAL_EVENT.HMAC_INSTRUCTIONS_BEFORE') }} <code class="font-mono">{{ SIGNATURE_HEADER }}</code> {{ t('FLOW_BUILDER.ACTION_FORMS.WAIT_EXTERNAL_EVENT.HMAC_INSTRUCTIONS_AFTER') }}
      </p>
      <code class="text-[10px] font-mono bg-n-solid-2 rounded px-2 py-1 text-n-slate-11 break-all select-all">
        {{ SIGNATURE_SAMPLE }}
      </code>

      <p class="text-xs font-medium text-n-slate-12 mt-1">{{ t('FLOW_BUILDER.ACTION_FORMS.WAIT_EXTERNAL_EVENT.YOUR_SECRET') }}</p>
      <code class="text-[10px] font-mono bg-n-solid-2 rounded px-2 py-1 text-n-slate-11 break-all select-all">
        {{ flowWebhookSecret }}
      </code>

      <p class="text-xs text-n-slate-9">
        {{ t('FLOW_BUILDER.ACTION_FORMS.WAIT_EXTERNAL_EVENT.PYTHON_EXAMPLE_BEFORE') }} <code class="font-mono">{{ PYTHON_SAMPLE }}</code>
      </p>
      <p class="text-xs text-n-slate-9 mt-1">
        {{ t('FLOW_BUILDER.ACTION_FORMS.WAIT_EXTERNAL_EVENT.CORRELATION_ID_BEFORE') }} <code class="font-mono">{{ CORRELATION_ID_TOKEN }}</code> {{ t('FLOW_BUILDER.ACTION_FORMS.WAIT_EXTERNAL_EVENT.CORRELATION_ID_MIDDLE') }} <code class="font-mono">{{ EXT_EVENT_ID_VAR }}</code> {{ t('FLOW_BUILDER.ACTION_FORMS.WAIT_EXTERNAL_EVENT.CORRELATION_ID_AFTER') }}
      </p>
    </div>
  </div>
</template>
